import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  myDayNotificationPort,
  myDayNotificationService,
} from '../features/my-day/notification-service';
import { MY_DAY_PATH } from '../features/my-day/my-day-route';

/**
 * MY DAY NOTIFICATION LIFECYCLE BRIDGE — WD-3.
 *
 * Mounted once inside `BrowserRouter` (beside `SystemBackBridge`, for the
 * same reason: it navigates). It owns exactly three wires:
 *
 *   1. **Startup**: one reconciliation run when the app mounts. Signed out,
 *      the authoritative read refuses and the run is a harmless no-op — no
 *      prompt, no schedule, no error surfaced.
 *   2. **Foreground**: the app returning to the foreground re-reconciles, so
 *      a snooze that lapsed, a change made on another device, or a reboot
 *      that emptied the OS schedule all converge on the next open. **This is
 *      the whole reboot posture** — no boot receiver; the schedule is rebuilt
 *      when Crewlink is next opened.
 *   3. **Tap**: tapping a Crewlink My Day notification navigates to My Day.
 *      **Navigation is ALL it does** — a notification is never a mutation,
 *      and there are no action buttons for one to hide in.
 *   4. **Session (review M2)**: the service watches the app's OWN Supabase
 *      session truth, and the SIGNED_OUT transition cancels this session's
 *      Crewlink alerts immediately — before any foreground or read.
 *      Reconciliation itself is auth-gated inside the service (a signed-out
 *      run schedules nothing, and every alert is scheduled FOR the current
 *      user), so mounting outside the React session provider is safe: the
 *      truth consulted is the same one the provider itself wraps.
 *
 * Lifecycle runs never prompt for permission (`mayPrompt` stays false here):
 * the contextual prompt belongs to the moment a person makes a timed
 * follow-up matter, on the My Day screen itself.
 */
export function MyDayNotificationsBridge() {
  const navigate = useNavigate();
  // `useNavigate` can return a new identity per location; the wires below are
  // registered ONCE, so the tap handler reads the current function through a
  // ref rather than re-mounting the whole bridge on every navigation (which
  // would re-run the startup reconciliation each time).
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    const port = myDayNotificationPort();
    const service = myDayNotificationService();

    // Off native every wire is an inert no-op; the run answers 'unsupported'.
    void service.run();
    const stopActive = port.onAppActive(() => {
      void service.run();
    });
    const stopTap = port.onTap(() => {
      void navigateRef.current(MY_DAY_PATH);
    });
    const stopSession = service.watchSession();

    return () => {
      stopActive();
      stopTap();
      stopSession();
    };
  }, []);

  return null;
}
